import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import * as z from "zod";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import { Eye, EyeClosed, Loader } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ROUTES } from "@/constants/routes";
import useAuth from "../../hooks/useAuth";
import { useState } from "react";
import { handleApiError } from "@/utils/errors";
import { catchError } from "@/lib/errors";
import { toast } from "sonner";

const formSchema = z.object({
  username: z.string().email(),
  password: z.string(),
});

const PasswordLoginForm = () => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { login } = useAuth();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  async function onSubmit(data: z.infer<typeof formSchema>) {
    setIsLoading(true);
    const [error] = await catchError(login(data));
    if (error) toast.error(handleApiError(error).message);
    setIsLoading(false);
  }

  return (
    <div className="w-full max-w-112.5 flex flex-col gap-12">
      <form id="login-form" onSubmit={form.handleSubmit(onSubmit)}>
        <FieldGroup>
          <Controller
            name="username"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="username-input">
                  {t("modules.auth.login_form.email")}
                </FieldLabel>
                <Input
                  {...field}
                  className="h-12"
                  id="username-input"
                  type="email"
                  aria-invalid={fieldState.invalid}
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />
          <div className="flex flex-col flex-1 gap-y-2">
            <Controller
              name="password"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel htmlFor="password-input">
                    {t("modules.auth.login_form.password")}
                  </FieldLabel>
                  <InputGroup className="h-12">
                    <InputGroupInput
                      {...field}
                      aria-invalid={fieldState.invalid}
                      type={showPassword ? "text" : "password"}
                      id="password-input"
                    />
                    <InputGroupAddon
                      align="inline-end"
                      className="cursor-pointer select-none"
                      onClick={() => setShowPassword((prev) => !prev)}
                    >
                      {showPassword ? <EyeClosed /> : <Eye />}
                    </InputGroupAddon>
                  </InputGroup>
                  {fieldState.invalid && (
                    <FieldError errors={[fieldState.error]} />
                  )}
                </Field>
              )}
            />
            <Link
              className="underline text-blue-600 text-right"
              to={ROUTES.FORGOT_PASSWORD}
            >
              {t("modules.auth.login_form.forgot_password")}
            </Link>
          </div>
        </FieldGroup>
      </form>
      <Button
        disabled={isLoading}
        type="submit"
        form="login-form"
        className="h-16 text-lg"
      >
        {isLoading && <Loader className="animate-spin" />}
        {t("modules.auth.login_form.submit")}
      </Button>
    </div>
  );
};

export default PasswordLoginForm;
